import { supabase } from './supabaseClient';

/**
 * Load restaurants based on user role
 */
export async function loadRestaurants(currentUser) {
  if (!supabase || !currentUser) return [];
  
  try {
    let query = supabase.from('restaurants').select('*');
    
    // Role-based filtering
    if (currentUser.role === 'marketer') {
      query = query.eq('assigned_to', currentUser.id);
    } else if (currentUser.role === 'rep') {
      query = query.eq('assigned_rep', currentUser.id);
    } else if (currentUser.role === 'data_entry') {
      query = query.eq('workflow_status', 'data_review');
    } else if (currentUser.role === 'accountant') {
      query = query.eq('workflow_status', 'invoice_pending');
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Transform snake_case to camelCase
    return (data || []).map(r => ({
      id: r.id,
      name: r.name,
      ownerName: r.owner_name,
      phone: r.phone,
      area: r.area,
      mapsUrl: r.maps_url,
      notes: r.notes,
      status: r.workflow_status || 'new',
      assignedTo: r.assigned_to,
      assignedRep: r.assigned_rep,
      assignedByRep: r.assigned_by_rep,
      visitDate: r.visit_date,
      visitNotes: r.visit_notes,
      visitReportUrl: r.visit_report_url,
      invoiceNotes: r.invoice_notes,
      files: {
        logoUrl: r.logo_url,
        menu1Url: r.menu_url_1,
        menu2Url: r.menu_url_2,
        invoiceUrl: r.invoice_url,
        tradeLicenseUrl: r.trade_license_url,
        ownerIdUrl: r.owner_id_url,
      },
      createdAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  } catch (err) {
    console.error('❌ Error loading restaurants:', err);
    throw err;
  }
}

/**
 * Create a new restaurant
 */
export async function createRestaurant(restaurant, currentUser) {
  if (!supabase) return null;
  
  try {
    const dbData = {
      name: restaurant.name,
      owner_name: restaurant.ownerName,
      phone: restaurant.phone,
      area: restaurant.area,
      maps_url: restaurant.mapsUrl,
      notes: restaurant.notes,
      workflow_status: restaurant.status || 'new',
      assigned_to: restaurant.assignedTo || null,
      created_by: currentUser.id,
    };
    
    const { data, error } = await supabase
      .from('restaurants')
      .insert([dbData])
      .select()
      .single();
    
    if (error) throw error;
    
    return data;
  } catch (err) {
    console.error('❌ Error creating restaurant:', err);
    throw err;
  }
}

/**
 * Update restaurant with activity logging
 */
export async function updateRestaurant(id, changes, currentUser) {
  if (!supabase) return false;
  
  try {
    // Map camelCase to snake_case
    const dbChanges = {};
    if (changes.name !== undefined) dbChanges.name = changes.name;
    if (changes.ownerName !== undefined) dbChanges.owner_name = changes.ownerName;
    if (changes.phone !== undefined) dbChanges.phone = changes.phone;
    if (changes.area !== undefined) dbChanges.area = changes.area;
    if (changes.mapsUrl !== undefined) dbChanges.maps_url = changes.mapsUrl;
    if (changes.notes !== undefined) dbChanges.notes = changes.notes;
    if (changes.status !== undefined) dbChanges.workflow_status = changes.status;
    if (changes.assignedTo !== undefined) dbChanges.assigned_to = changes.assignedTo;
    if (changes.assignedRep !== undefined) dbChanges.assigned_rep = changes.assignedRep;
    if (changes.visitDate !== undefined) dbChanges.visit_date = changes.visitDate;
    if (changes.visitNotes !== undefined) dbChanges.visit_notes = changes.visitNotes;
    if (changes.invoiceNotes !== undefined) dbChanges.invoice_notes = changes.invoiceNotes;
    if (changes.files) {
      if (changes.files.logoUrl !== undefined) dbChanges.logo_url = changes.files.logoUrl;
      if (changes.files.menu1Url !== undefined) dbChanges.menu_url_1 = changes.files.menu1Url;
      if (changes.files.menu2Url !== undefined) dbChanges.menu_url_2 = changes.files.menu2Url;
      if (changes.files.invoiceUrl !== undefined) dbChanges.invoice_url = changes.files.invoiceUrl;
      if (changes.files.tradeLicenseUrl !== undefined) dbChanges.trade_license_url = changes.files.tradeLicenseUrl;
      if (changes.files.ownerIdUrl !== undefined) dbChanges.owner_id_url = changes.files.ownerIdUrl;
    }
    
    const { error } = await supabase
      .from('restaurants')
      .update(dbChanges)
      .eq('id', id);
    
    if (error) throw error;
    
    // Log activity (non-blocking)
    await logActivity({
      user_id: currentUser.id,
      user_role: currentUser.role,
      action_type: changes.status ? 'status_change' : 'update',
      table_name: 'restaurants',
      record_id: id,
      record_name: changes.name,
      details: { changes: dbChanges },
    });
    
    return true;
  } catch (err) {
    console.error('❌ Error updating restaurant:', err);
    throw err;
  }
}

/**
 * Delete restaurant
 */
export async function deleteRestaurant(id) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('restaurants')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (err) {
    console.error('❌ Error deleting restaurant:', err);
    throw err;
  }
}

/**
 * Log activity (silent failure - doesn't block main operation)
 */
async function logActivity(activity) {
  try {
    await supabase.from('activity_log').insert([activity]);
  } catch (err) {
    console.error('⚠️ Failed to log activity:', err);
    // Don't throw - logging shouldn't block the main operation
  }
}
